import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import CustomerServiceModal from '../contact/CustomerServiceModal';

// 定义动画关键帧
const slideInFromRight = keyframes`
  0% { 
    right: -60px;
    opacity: 0;
  }
  100% { 
    right: 32px;
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  0% { 
    right: 32px;
  }
  100% { 
    right: -28px;
  }
`;

const slideInFromHidden = keyframes`
  0% { 
    right: -28px;
  }
  100% { 
    right: 32px;
  }
`;

const pulse = keyframes`
  0% { 
    box-shadow: 0 0 0 0 rgba(67, 83, 255, 0.4);
  }
  70% { 
    box-shadow: 0 0 0 8px rgba(67, 83, 255, 0);
  }
  100% { 
    box-shadow: 0 0 0 0 rgba(67, 83, 255, 0);
  }
`;

const ENABLE_FESTIVAL_MODE = false; // 春节/节日模式开关

const FloatingCustomerService = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [status, setStatus] = useState('online');
  const [modalOpen, setModalOpen] = useState(false);
  const [isInitialShow, setIsInitialShow] = useState(true);
  const [isRecoveringFromHidden, setIsRecoveringFromHidden] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);

  // 根据模式固定文字
  const displayText = ENABLE_FESTIVAL_MODE ? '分享有礼' : '在线客服';

  // 使用ref来管理定时器
  const hideTimeoutRef = useRef(null);
  const autoHideTimeoutRef = useRef(null);

  // 初始显示动画
  useEffect(() => {
    if (ENABLE_FESTIVAL_MODE) return; // 节日模式下由循环逻辑控制显示

    const showTimeout = setTimeout(() => {
      setIsVisible(true);
      setTimeout(() => {
        setIsInitialShow(false);
      }, 800);
    }, 2000);

    return () => clearTimeout(showTimeout);
  }, []);

  // 节日模式循环逻辑
  useEffect(() => {
    if (!ENABLE_FESTIVAL_MODE) return;

    let isMounted = true;

    const runCycle = async () => {
      // 这里的逻辑：如果是重置进来的（不是初始挂载），先等10秒
      if (resetCounter > 0) {
        await new Promise(r => setTimeout(r, 10000));
      }

      while (isMounted) {
        // 1. 滑出来
        setIsVisible(true);
        setIsHidden(false);
        setIsInitialShow(true);

        await new Promise(r => setTimeout(r, 800)); // 等待划入动画
        if (!isMounted) break;
        setIsInitialShow(false);

        // 2. 持续停留并展示 (总共停留 7s)
        await new Promise(r => setTimeout(r, 7000));
        if (!isMounted) break;

        // 7. 滑回去
        setIsHidden(true);

        // 8. 隐藏10s
        await new Promise(r => setTimeout(r, 10000));
        if (!isMounted) break;
      }
    };

    runCycle();

    return () => {
      isMounted = false;
    };
  }, [resetCounter]);

  // 自动隐藏逻辑 (非节日模式)
  useEffect(() => {
    if (ENABLE_FESTIVAL_MODE || !isVisible || isHovered || isInitialShow) return;

    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
    }

    autoHideTimeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsHidden(true);
        setIsRecoveringFromHidden(false);
      }
    }, 5000);

    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, [isVisible, isHovered, isInitialShow]);

  // 智能状态变化
  useEffect(() => {
    const updateStatus = () => {
      const hour = new Date().getHours();
      if (hour >= 9 && hour < 21) {
        setStatus('online');
      } else {
        setStatus('offline');
      }
    };
    const interval = setInterval(updateStatus, 60000);
    updateStatus();
    return () => clearInterval(interval);
  }, []);

  // 重置恢复状态（动画完成后）
  useEffect(() => {
    if (isRecoveringFromHidden && !isHidden) {
      const resetTimeout = setTimeout(() => {
        setIsRecoveringFromHidden(false);
      }, 300);
      return () => clearTimeout(resetTimeout);
    }
  }, [isRecoveringFromHidden, isHidden]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    if (isHidden) {
      setIsRecoveringFromHidden(true);
    }

    setIsHovered(true);
    setIsHidden(false);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsRecoveringFromHidden(false);

    if (ENABLE_FESTIVAL_MODE) return; // 节日模式下不介入鼠标离开隐藏逻辑

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsHidden(true);
      setIsRecoveringFromHidden(false);
      hideTimeoutRef.current = null;
    }, 3000);
  };

  const handleClick = () => {
    setModalOpen(true);
    if (ENABLE_FESTIVAL_MODE) {
      setIsHidden(true);
      setResetCounter(prev => prev + 1);
      // 用户主动点击后，隐藏10s后再划落出来
      // 这里通过重置初始显示状态和隐藏状态，并由循环逻辑控制
    }
  };

  const getAnimation = () => {
    if (!isVisible) return 'none';
    if (isInitialShow) return `${slideInFromRight} 0.8s ease-out`;
    if (isHidden) return `${slideOutToRight} 0.5s ease-out forwards`;
    if (isRecoveringFromHidden) return `${slideInFromHidden} 0.3s ease-out forwards`;
    return 'none';
  };

  if (!isVisible) return null;

  return (
    <>
      {/* 悬浮球主体 */}
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        sx={{
          position: 'fixed',
          right: '32px',
          top: '33%',
          transform: 'translateY(-50%)',
          zIndex: 9999,
          cursor: 'pointer',
          userSelect: 'none',
          animation: getAnimation(),
        }}
      >
        {/* 圣诞小帽子 (节日模式启用) */}
        {ENABLE_FESTIVAL_MODE && (
          <Box
            component="img"
            src="/images/hat.png"
            sx={{
              position: 'absolute',
              top: '-48px',
              left: '51%', // 稍微偏移一点以配合 -4deg 的视觉重心
              transform: 'translateX(-50%) rotate(-4deg)', // 加上轻微倾斜
              width: '75px',
              height: 'auto',
              zIndex: 10000,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))',
            }}
          />
        )}

        {/* 主悬浮球 */}
        <Box
          sx={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: ENABLE_FESTIVAL_MODE
              ? 'linear-gradient(135deg, #FF4D4D 0%, #FF0000 50%, #D40000 100%)'
              : 'linear-gradient(135deg, #4353FF 0%, #5A6BFF 50%, #6C7AFF 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: displayText === '分享有礼' ? '0px' : '1px',
            lineHeight: '1.2',
            boxShadow: ENABLE_FESTIVAL_MODE
              ? `
                0 0 0 1px rgba(255, 255, 255, 0.2) inset,
                0 2px 4px rgba(255, 0, 0, 0.2),
                0 6px 20px rgba(255, 0, 0, 0.3),
                0 12px 35px rgba(255, 0, 0, 0.15)
              `
              : `
                0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                0 2px 4px rgba(67, 83, 255, 0.2),
                0 6px 20px rgba(67, 83, 255, 0.3),
                0 12px 35px rgba(67, 83, 255, 0.15)
              `,
            transition: 'all 0.3s ease',
            animation: status === 'online' ? `${pulse} 2s infinite` : 'none',
            '&:hover': {
              transform: 'scale(1.05) translateY(-1px)',
              boxShadow: ENABLE_FESTIVAL_MODE
                ? '0 8px 25px rgba(255, 0, 0, 0.4)'
                : '0 8px 25px rgba(67, 83, 255, 0.4)',
            },
          }}
        >
          {displayText.length > 2 ? (
            <>
              <span>{displayText.substring(0, 2)}</span>
              <span>{displayText.substring(2)}</span>
            </>
          ) : (
            <span>{displayText}</span>
          )}
        </Box>

        {/* 状态指示器 */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: status === 'online' ? '#00C853' : '#9E9E9E',
            color: 'white',
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '9px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {status === 'online' ? '在线' : '离线'}
        </Box>
      </Box>

      {/* 客服模态框 */}
      <CustomerServiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={displayText}
        isFestival={ENABLE_FESTIVAL_MODE}
      />
    </>
  );
};

export default FloatingCustomerService;